import { environment } from '../../environments/environment';
import { extractTenantId } from './sso.types-helpers';
import {
  AppleSSOConfig,
  AuthProvider,
  GoogleSSOConfig,
  MicrosoftSSOConfig,
  SSOConfiguration,
} from './sso.types';

// Re-export the SSO type names so consumers can pull both values and
// types from a single module.
export type { AuthProvider, SSOConfiguration, MicrosoftSSOConfig, GoogleSSOConfig, AppleSSOConfig };

/**
 * SSO provider configuration.
 *
 * Values are sourced from `environment.<config>.ts` (selected at build
 * time via `fileReplacements` in `angular.json`). The policy on what
 * belongs in those files vs. `.env` is documented in
 * `src/environments/environment.ts` and `.config/README.md`.
 *
 * What lives in environment.<env>.ts (per-environment values):
 *   - URLs (REST API, WebSocket, MSAL authority, redirect URIs)
 *   - Public OAuth client IDs (MSAL/GIS/Apple — not secrets by spec)
 *   - Scope strings, tenant IDs
 *   - Feature flags
 *
 * What lives in `.env` files (real secrets):
 *   - MSAL_CLIENT_SECRET, WEATHER_API_KEY, IPSTACK_API_KEY,
 *     WEBSOCKET_SERVER_TOKEN, SESSION_FALLBACK_TOKEN, SMTP_PASSWORD
 *   - Any value that authenticates the bearer to a backend service
 */
export const MICROSOFT_SSO_CONFIG: MicrosoftSSOConfig = {
  enabled: true,
  clientId: environment.msal.clientId,
  tenantId: environment.msal.tenantId || extractTenantId(environment.msal.authority),
  authority: environment.msal.authority,
  scopes: environment.msal.scopes,
  apiScope: environment.msal.apiScope,
  redirectUri: environment.msal.redirectUri,
  postLogoutRedirectUri: environment.msal.postLogoutRedirectUri ?? environment.msal.redirectUri,
  navigateToLoginRequestUrl: false,
  cacheLocation: 'localStorage',
  interactionType: 'redirect',
};

export const GOOGLE_SSO_CONFIG: GoogleSSOConfig = {
  enabled: true,
  clientId: environment.google.clientId,
  redirectUri: environment.google.redirectUri,
  scopes: environment.google.scopes,
  cookiePolicy: 'single_host_origin',
  ux_mode: 'popup',
  auto_select: false,
};

export const APPLE_SSO_CONFIG: AppleSSOConfig = {
  enabled: true,
  clientId: environment.apple.clientId,
  scope: environment.apple.scopes,
  redirectURI: environment.apple.redirectUri,
  state: '',
  nonce: '',
  usePopup: true,
};

export const SSO_CONFIG: SSOConfiguration = {
  microsoft: MICROSOFT_SSO_CONFIG,
  google: GOOGLE_SSO_CONFIG,
  apple: APPLE_SSO_CONFIG,
  common: {
    defaultProvider: 'microsoft',
    enableMultiProvider: true,
    sessionTimeout: 480,
    rememberMe: true,
    debugMode: !environment.production,
    apiEndpoints: [environment.apiUrl],
  },
};

export function getApiBaseUrl(): string {
  return environment.apiUrl;
}

export function getAssetsBaseUrl(): string {
  return environment.assetsBaseUrl;
}

export function getWebsocketUrl(): string {
  return environment.wsServer;
}

export function getRedirectUri(): string {
  return environment.msal.redirectUri;
}

export function getProtectedApiEndpoints(): string[] {
  return [environment.apiUrl];
}

export function isProviderEnabled(provider: 'microsoft' | 'google' | 'apple'): boolean {
  return SSO_CONFIG[provider].enabled;
}

export function isDebugMode(): boolean {
  return SSO_CONFIG.common.debugMode;
}

export function getEnabledProviders(): Array<'microsoft' | 'google' | 'apple'> {
  return (['microsoft', 'google', 'apple'] as const).filter((p) => SSO_CONFIG[p].enabled);
}

export function getProviderConfig(
  provider: 'microsoft' | 'google' | 'apple',
): MicrosoftSSOConfig | GoogleSSOConfig | AppleSSOConfig {
  return SSO_CONFIG[provider];
}
